import { css } from '@emotion/react';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { usePageTitle } from '../Lib';

import { ANALYTICS_EVENTS, NEW_EXPERIENCE_ANALYTICS, setNewExperienceAnalyticsContext, trackEvent } from '@/Client/analytics';
import { PageLayout } from '@/Client/Layout';
import { isOptedIntoNewExperience, optIntoNewExperience, optOutOfNewExperience } from '@/Client/Lib/newExperienceLib';
import { useAuth, useClearCache, useOrgs, useSuppliersModal } from '@/Client/State';
import { LAYOUT2 } from '@/Client/Styles';
import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { Button, CustomToggle } from '@/Client/Widgets';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';
import { CacheInfoModal, SettingsModal } from '../Partials';

const PAGE_BACKGROUND_STYLE = css`
  min-height: 100%;
  padding: 18px 20px 120px;
  background: ${styleguideColors.shell};
`;

const CARD_STYLE = css`
  width: 640px;
  background: ${styleguideColors.paper};
  border: 1px solid ${styleguideColors.stroke};
  padding: 0 26px;
  color: ${styleguideColors.ink};
  font-family: ${styleguideFontFamily.body};
`;

const INFO_ROW_STYLE = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 19px 0;
  border-bottom: 1px solid ${styleguideColors.stroke};
`;

const INFO_LABEL_STYLE = css`
  font-size: ${pxToRem(14)};
  font-weight: 700;
  flex-shrink: 0;
`;

const INFO_VALUE_STYLE = css`
  font-size: ${pxToRem(14)};
  text-align: right;
  word-break: break-all;
`;

const INFO_VALUE_MONO_STYLE = css`
  ${INFO_VALUE_STYLE};
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(13)};
`;

const OPT_IN_ROW_STYLE = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 26px;
  padding: 21px 0 20px 0;
  border-bottom: 1px solid ${styleguideColors.stroke};
`;

const OPT_IN_LEFT_STYLE = css`
  flex: 1;
  min-width: 0;
`;

const OPT_IN_LABEL_STYLE = css`
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(14)};
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 7px;
`;

const BETA_BADGE_STYLE = css`
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(8.5)};
  background: ${styleguideColors.ink};
  color: ${styleguideColors.paper};
  letter-spacing: 0.1em;
  padding: 3px 6px 2px;
  line-height: 1.2;
`;

const OPT_IN_DESCRIPTION_STYLE = css`
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(12.5)};
  line-height: 1.5;
  color: ${styleguideColors.midtone};
  max-width: 376px;
`;

const OPT_IN_CONTROLS_STYLE = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 9px;
  flex-shrink: 0;
  padding-top: 1px;
`;

const OPT_IN_HELP_STYLE = css`
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(10.5)};
  line-height: 1.5;
  color: ${styleguideColors.textTertiary};
  text-align: right;
  max-width: 132px;
`;

const OPT_IN_HELP_VALUE_STYLE = css`
  color: ${styleguideColors.midtone};
  font-weight: 500;
`;

const ORGS_HEADER_STYLE = css`
  font-size: ${pxToRem(14)};
  font-weight: 700;
  padding: 20px 0 13px;
  border-bottom: 1px solid ${styleguideColors.stroke};
`;

const ORG_ITEM_STYLE = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 13px 0;
  margin-left: 20px;
  border-bottom: 1px solid ${styleguideColors.stroke};

  &:last-child {
    border-bottom: none;
  }
`;

const ORG_INFO_STYLE = css`
  font-size: ${pxToRem(13.5)};
`;

const CURRENT_ORG_INFO_STYLE = css`
  font-size: ${pxToRem(13.5)};
  font-weight: 700;
`;

const BUTTONS_BAR_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  padding: ${LAYOUT2.spacing2}px 0;
  border-top: 1px solid ${styleguideColors.stroke};

  > button {
    flex: 0 0 auto;
    padding: 0 ${LAYOUT2.spacing1}px !important;
    font-size: ${pxToRem(11)} !important;
    letter-spacing: 0 !important;
    white-space: nowrap;
  }
`;

const BUTTONS_BAR_ROW = css`
  display: flex;
  flex-direction: row;
  gap: ${LAYOUT2.spacing1}px;
`;

export const UserAccountPage = () => {
  const { credentials, credentialsMutators, user, isSuperAdmin } = useAuth();
  const { orgs } = useOrgs();
  const navigate = useNavigate();
  usePageTitle('My Profile');
  const [showCacheInfoModal, setShowCacheInfoModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const { openSuppliersModal } = useSuppliersModal();
  const { featureFlags } = useFeatureFlags();
  const [optedIntoNewExperience, setOptedIntoNewExperience] = useState(() => isOptedIntoNewExperience(featureFlags));
  const clearCache = useClearCache();

  React.useEffect(() => {
    if (!credentials.token) navigate('/');
  }, [credentials.token, navigate]);

  const switchToOrg = async (orgId: string) => {
    await credentialsMutators.switchOrg(orgId);
    await clearCache();
    setTimeout(() => window.location.reload(), 1000);
  };

  const switchBackToOriginalUser = async () => {
    await credentialsMutators.restoreOriginalUser();
    await clearCache();
    setTimeout(() => window.location.reload(), 1000);
  };

  const toggleNewExperienceOptIn = () => {
    if (optedIntoNewExperience) {
      optOutOfNewExperience();
    } else {
      optIntoNewExperience();
    }
    setNewExperienceAnalyticsContext(!optedIntoNewExperience);
    trackEvent(
      optedIntoNewExperience ? ANALYTICS_EVENTS.newExperienceDisabled : ANALYTICS_EVENTS.newExperienceEnabled,
      { newExperienceChangeSource: NEW_EXPERIENCE_ANALYTICS.changeSources.accountToggle },
    );
    setOptedIntoNewExperience(!optedIntoNewExperience);
  };

  return (
    <PageLayout
      title='My Profile'
      contentLayoutStyling='none'
    >
      <div css={PAGE_BACKGROUND_STYLE}>
        <div css={CARD_STYLE}>
          <div>
            <div css={INFO_ROW_STYLE}>
              <span css={INFO_LABEL_STYLE}>Name:</span>
              <span css={INFO_VALUE_STYLE}>{user?.name}</span>
            </div>
            <div css={INFO_ROW_STYLE}>
              <span css={INFO_LABEL_STYLE}>Phone Number:</span>
              <span css={INFO_VALUE_STYLE}>{user?.phoneNumber}</span>
            </div>
            <div css={INFO_ROW_STYLE}>
              <span css={INFO_LABEL_STYLE}>Email:</span>
              <span css={INFO_VALUE_STYLE}>{user?.email}</span>
            </div>
            <div css={INFO_ROW_STYLE}>
              <span css={INFO_LABEL_STYLE}>User ID:</span>
              <span css={INFO_VALUE_MONO_STYLE}>{user?.id}</span>
            </div>
            <div css={INFO_ROW_STYLE}>
              <span css={INFO_LABEL_STYLE}>Org ID:</span>
              <span css={INFO_VALUE_MONO_STYLE}>{credentials.orgId}</span>
            </div>
            {!!featureFlags.newScopeBuilderFlow && (
              <div css={OPT_IN_ROW_STYLE}>
                <div css={OPT_IN_LEFT_STYLE}>
                  <div css={OPT_IN_LABEL_STYLE}>
                    New Kai Experience
                    <span css={BETA_BADGE_STYLE}>BETA</span>
                  </div>
                  <div css={OPT_IN_DESCRIPTION_STYLE}>
                    Scope, review, and share documents faster! Your projects and scopes stay the same, so you can switch back and forth without losing work.
                  </div>
                </div>
                <div css={OPT_IN_CONTROLS_STYLE}>
                  <CustomToggle
                    isOn={optedIntoNewExperience}
                    onToggle={toggleNewExperienceOptIn}
                    alwaysDark={false}
                  />
                  <div css={OPT_IN_HELP_STYLE}>
                    Currently viewing:<br />
                    <span css={OPT_IN_HELP_VALUE_STYLE}>
                      {optedIntoNewExperience ? 'New Kai Experience' : 'Original experience'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <div css={ORGS_HEADER_STYLE}>Your Organizations:</div>
            {[...orgs].sort((a, b) => a.name.localeCompare(b.name)).map(org => (
              <div key={org.id} css={ORG_ITEM_STYLE}>
                <span css={org.id === credentials.orgId ? CURRENT_ORG_INFO_STYLE : ORG_INFO_STYLE}>
                  {org.name} ({org.role})
                </span>
                {org.id === credentials.orgId && credentials.impersonationBackupToken
                  ? (
                    <Button onClick={switchBackToOriginalUser} buttonStyle='darkOrange'>
                      Back to Original User
                    </Button>
                  )
                  : org.id !== credentials.orgId && (
                    <Button onClick={() => switchToOrg(org.id)}>
                      Switch to this Org
                    </Button>
                  )}
              </div>
            ))}
          </div>

          <div css={BUTTONS_BAR_STYLE}>
            <div css={BUTTONS_BAR_ROW}>
              <Button onClick={() => setShowSettingsModal(true)}>Legacy Settings</Button>
              <Button onClick={() => setShowCacheInfoModal(true)} buttonStyle='blue'>Cache Info</Button>
              {isSuperAdmin && <Button buttonStyle='darkYellow' onClick={openSuppliersModal}>{'Suppliers'}</Button>}
            </div>
          </div>
        </div>
      </div>

      <SettingsModal
        visible={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      <CacheInfoModal
        visible={showCacheInfoModal}
        onClose={() => setShowCacheInfoModal(false)}
      />
    </PageLayout>
  );
};
